/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Element from "./element";
import {BoundingBox as RenderBoundingBox, RenderDocument, SurfaceColor, LineProperties, RenderOption, EEmbededElementTypes} from "js_renderer";
import AbstractElement from "./abstract_element";
import Factory from "./factory";
import Document from "../structural_elements/document"
import {vec3} from "gl-matrix"

/***
 *
 * @param aSceneAPI
 * @constructor
 */
export default
class Curve extends Element {

    public static TypeName: string = EEmbededElementTypes.CURVE;
    protected _bbox: RenderBoundingBox;
    protected _positions: number[];
    protected _indices: number[];
    private _highlighCurveRenderElement: AbstractElement;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._bbox = null;
        this._positions = [];
        this._indices = [];

        this.extendProperties(
            {
                surfaceColor: SurfaceColor.getDefaultValues(),
                lineProperties: LineProperties.getDefaultValues(),
                renderOption: RenderOption.getDefaultValues(),
                contributeToNearPlaneClipping: true,
                clippable: true,
                notOutline: false
            }
        );

        this._highlighCurveRenderElement = null;
    }


    public static create(aRenderDocument: RenderDocument): Curve {
        let element = new Curve(aRenderDocument);
        element._typeName = Curve.TypeName;
        return element;
    }

    /***
     * Highlight is a different implementation of select method which just change color. With highlight we implement a special behavior
     * of selection so that the highlighted curve is drawn in bold.
     *
     * @param aValue true when highlight is enabled, false otherwise
     */
    public highlight(aValue: boolean) {
        if (!aValue) {
            this._removeHighlighting();
            return;
        }

        if (this._highlighCurveRenderElement) {
            // already highlighted
            return;
        }

        // create highlighting model element
        let renderModel = this._renderDocument.getInternalModel();

        this._highlighCurveRenderElement = renderModel.addElement(
            Document.getInternalGroupID(),
            this.getTypeName(),
            this._generateElementDescriptionJSON(),
            this._generateHiglighterGeometryObjectDescriptionJSON(),
            true
        );
    }

    /***
     * Animated zoom closer to the point.
     *
     * @param aFocusFactor A distance to which the camera will zoom in (a bounding box diameter * aFocusFactor)
     * @param aAnimationTime a Length of animated transition from current camera position to the focus point in seconds.
     */
    public focus(aFocusFactor: number, aAnimationTime: number) {
        this._sceneAPI.viewModeModule.focusToPosition(this._bbox.getCenter(), aFocusFactor, aAnimationTime);
    }

    private _removeHighlighting() {
        if (this._highlighCurveRenderElement) {
            this._renderDocumentProperties.renderModel.removeElement(this._highlighCurveRenderElement);
        }

        this._highlighCurveRenderElement = null;
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),        // line is different from curve as it has only two points
            "id": this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            "UID": this._UID
        };
    }

    public setPositions(aPositions: number[], aEPSGCode?: string) {
        this._positions = [];
        this._indices = [];
        for (let i = 0, j = 0; i < aPositions.length; i += 3, j++) {
            this._positions.push(aPositions[i + 0]);
            this._positions.push(aPositions[i + 1]);
            this._positions.push(aPositions[i + 2]);
            this._indices.push(j);
        }

        if (this._renderDocumentProperties.renderElement && this._renderDocumentProperties.renderElement.getGeometryObjects()[0].getRenderAsOutline()) {
            this.updatePositions([
                new Float32Array(this._positions)
            ]);
        }
    }

    public setPositionsFromLineStripLoop(aPositions: number[]) {
        this._positions = [];
        this._indices = [];
        for (let i = 0; i < aPositions.length; i += 3) {
            this._positions.push(aPositions[i + 0]);
            this._positions.push(aPositions[i + 1]);
            this._positions.push(aPositions[i + 2]);
            this._indices.push(this._indices.length);
        }

        // start offseted from second vertex
        for (let i = 3; i < aPositions.length; i += 3) {
            this._positions.push(aPositions[i + 0]);
            this._positions.push(aPositions[i + 1]);
            this._positions.push(aPositions[i + 2]);
            this._indices.push(this._indices.length);
        }

        // finish the loop with first vertex
        this._positions.push(aPositions[0]);
        this._positions.push(aPositions[1]);
        this._positions.push(aPositions[2]);
        this._indices.push(this._indices.length);

        if (this._renderDocumentProperties.renderElement && this._renderDocumentProperties.renderElement.getGeometryObjects()[0].getRenderAsOutline()) {
            this.updatePositions([
                new Float32Array(this._positions)
            ]);
        }
    }

    /***
     * We generate the base64 encoded binary data here as we would like to use the binaryGeometry class which accepts as input a response which is an array with data
     * @returns {{description: {id: string, type: string, bboxMin: *, bboxMax: *, color: Array}, appearance: {renderOption: {alwaysOnTop: boolean, pickable: boolean}}, binaryGeometry: {vertexCount: number, indexCount: number, offset: number, binSize: number, src: string}}}
     * @private
     */
    protected _generateGeometryObjectDescriptionJSON() {
        // compute bbox from positions
        this._bbox = new RenderBoundingBox();
        for (let i = 0; i < this._positions.length; i += 3) {
            this._bbox.extendByXYZ(this._positions[i + 0], this._positions[i + 1], this._positions[i + 2]);
        }

        let min = vec3.clone(this._bbox.getMin());
        let max = vec3.clone(this._bbox.getMax());

        return [{
            description: {
                type: "LINE",
                bboxMin: [min[0], min[1], min[2]],
                bboxMax: [max[0], max[1], max[2]],
                contributeToNearPlaneClipping: this._properties.contributeToNearPlaneClipping
            },
            appearance: {
                material: {
                    diffuseColor: this._properties.surfaceColor.diffuseColor,
                    ambientColor: this._properties.surfaceColor.ambientColor,
                    emissiveColor: this._properties.surfaceColor.emissiveColor,
                    specularColor: this._properties.surfaceColor.specularColor,
                    shininess: this._properties.surfaceColor.shininess,
                    transparency: this._properties.surfaceColor.transparency
                },
                lineProperties: {
                    linetype: this._properties.lineProperties.linetype,
                    linewidthScaleFactor: this._properties.lineProperties.linewidthScaleFactor,
                    clippable: this._properties.clippable,
                    notOutline: this._properties.notOutline // it must be here because when a is rendered as outline, the reneder options node has two renderbaes (line properteis and render option and teh properteis of the renderOptions is rewritten via the lineProperties options)
                },
                renderOption: {
                    polygonOffsetFactor: this._properties.renderOption.polygonOffsetFactor,
                    polygonOffsetUnits: this._properties.renderOption.polygonOffsetUnits,
                    clippable: this._properties.clippable,
                    notOutline: this._properties.notOutline
                }
            },
            indexedLineSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: this._positions.length / 3,
                indexCount: this._indices.length,
                binSize: 0,
                positions: this._positions,
                indices: this._indices
            }
        }];
    }

    /***
     * We generate the base64 encoded binary data here as we would like to use the binaryGeometry class which accepts as input a response which is an array with data
     * @returns {{description: {id: string, type: string, bboxMin: *, bboxMax: *, color: Array}, appearance: {renderOption: {alwaysOnTop: boolean, pickable: boolean}}, binaryGeometry: {vertexCount: number, indexCount: number, offset: number, binSize: number, src: string}}}
     * @private
     */
    protected _generateHiglighterGeometryObjectDescriptionJSON(): any {
        let sourceGeometryObjects = this._renderDocumentProperties.renderElement.getGeometryObjects();

        if (sourceGeometryObjects.length === 0) {
            return [];
        }

        // we expect that we have only one geometry object per model element i.e. the curve has same attributes over its whole length
        let sourceGeometryObject = sourceGeometryObjects[0];

        return [{
            description: {
                type: "LINE",
                bboxMin: [sourceGeometryObject.properties.bboxMin[0], sourceGeometryObject.properties.bboxMin[1], sourceGeometryObject.properties.bboxMin[2]],
                bboxMax: [sourceGeometryObject.properties.bboxMax[0], sourceGeometryObject.properties.bboxMax[1], sourceGeometryObject.properties.bboxMax[2]],
                contributeToNearPlaneClipping: this._properties.contributeToNearPlaneClipping
            },
            appearance: {
                material: {
                    diffuseColor: sourceGeometryObject.appearance.surfaceColor.diffuseColor,
                    ambientColor: sourceGeometryObject.appearance.surfaceColor.ambientColor,
                    emissiveColor: sourceGeometryObject.appearance.surfaceColor.emissiveColor,
                    specularColor: sourceGeometryObject.appearance.surfaceColor.specularColor,
                    shininess: sourceGeometryObject.appearance.surfaceColor.shininess,
                    transparency: sourceGeometryObject.appearance.surfaceColor.transparency
                },
                lineProperties: {
                    linetype: sourceGeometryObject.appearance.lineProperties.linetype,
                    linewidthScaleFactor: sourceGeometryObject.appearance.lineProperties.linewidthScaleFactor + 5.0
                },
                renderOption: {
                    polygonOffsetFactor: sourceGeometryObject.appearance.renderOption.polygonOffsetFactor,
                    polygonOffsetUnits: sourceGeometryObject.appearance.renderOption.polygonOffsetUnits
                }
            },
            indexedLineSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: sourceGeometryObject.geometry.properties.vertexCount,
                indexCount: sourceGeometryObject.geometry.properties.indexCount,
                binSize: sourceGeometryObject.geometry.properties.binSize,
                positions: sourceGeometryObject.geometry.positionsDesc.data,
                indices: sourceGeometryObject.geometry.indicesDesc.data
            }
        }];
    }
}

Factory.register(Curve);
